import React, { useEffect, useState } from 'react';
import { AlertTriangle, Download, FileText, X } from 'lucide-react';
import { getThemeColor } from '../../config/appConfig';
import NewtonsCradleLoader from '../../components/common/NewtonsCradleLoader';
import { saveBlob } from '../../utils/partnerAgreements';

const THEME = getThemeColor('primary');

/**
 * Fetches a PDF through the authorized API (loadPdf → Blob) and shows it in the browser's built-in PDF viewer.
 * Also used inline (embedded) by the sign dialog for the review step.
 */
export const AgreementPdfFrame = ({ loadPdf, title, className = 'h-[65vh]', onBlob }) => {
  const [state, setState] = useState({ loading: true, error: '', url: '' });

  useEffect(() => {
    let cancelled = false;
    let objectUrl = '';
    setState({ loading: true, error: '', url: '' });
    loadPdf()
      .then((blob) => {
        if (cancelled) return;
        objectUrl = URL.createObjectURL(blob);
        onBlob?.(blob);
        setState({ loading: false, error: '', url: objectUrl });
      })
      .catch((error) => {
        if (!cancelled) setState({ loading: false, error: error.message || 'Unable to load the document', url: '' });
      });
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
    // loadPdf identity changes only when the document changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadPdf]);

  if (state.loading) {
    return (
      <div className={`flex items-center justify-center rounded-xl border border-gray-200 bg-gray-50 ${className}`}>
        <NewtonsCradleLoader size="28px" />
      </div>
    );
  }

  if (state.error) {
    return (
      <div className={`flex flex-col items-center justify-center rounded-xl border border-gray-200 bg-gray-50 px-6 text-center ${className}`}>
        <AlertTriangle className="h-8 w-8 mb-2 text-red-600" />
        <p className="text-sm font-semibold text-gray-900">Document unavailable</p>
        <p className="text-sm text-gray-500 mt-1">{state.error}</p>
      </div>
    );
  }

  return <iframe title={title} src={state.url} className={`w-full rounded-xl border border-gray-200 bg-white ${className}`} />;
};

const AgreementPdfModal = ({ isOpen, title, subtitle, fileName, loadPdf, onClose }) => {
  const [blob, setBlob] = useState(null);

  useEffect(() => {
    if (!isOpen) setBlob(null);
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[10000] flex items-center justify-center p-4">
      <button type="button" className="absolute inset-0 bg-black/50" onClick={onClose} aria-label="Close document" />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="agreement-pdf-title"
        className="relative w-full max-w-5xl rounded-2xl bg-white shadow-xl border border-gray-100 overflow-hidden"
      >
        <div className="flex items-start justify-between gap-4 px-5 sm:px-6 pt-5 pb-4 border-b border-gray-100">
          <div className="flex items-start gap-3 min-w-0">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0 bg-red-50">
              <FileText className="w-5 h-5" style={{ color: THEME }} />
            </div>
            <div className="min-w-0">
              <h2 id="agreement-pdf-title" className="text-base font-semibold text-gray-900 truncate">{title}</h2>
              {subtitle ? <p className="text-xs text-gray-500 mt-0.5 truncate">{subtitle}</p> : null}
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <button
              type="button"
              onClick={() => blob && saveBlob(blob, fileName)}
              disabled={!blob}
              className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border-2 text-sm font-semibold hover:bg-red-50 transition disabled:opacity-40"
              style={{ borderColor: THEME, color: THEME }}
            >
              <Download className="w-4 h-4" />
              <span className="hidden sm:inline">Download</span>
            </button>
            <button
              type="button"
              onClick={onClose}
              className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition"
              aria-label="Close"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
        <div className="p-4 sm:p-5">
          <AgreementPdfFrame loadPdf={loadPdf} title={title} onBlob={setBlob} />
        </div>
      </div>
    </div>
  );
};

export default AgreementPdfModal;
